/** Pure time helpers for the announcement composer (workspace time zone ↔ UTC ISO). */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const EXPIRY_OPTIONS = ["2d", "1w", "endOfMonth", "never"] as const;
export type ExpiryOption = (typeof EXPIRY_OPTIONS)[number];

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function zonedParts(instant: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: value("year"), month: value("month"), day: value("day"), hour: value("hour"), minute: value("minute") };
}

/** Milliseconds the zone is ahead of UTC at `instant` (Asia/Manila → +8 h). */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const p = zonedParts(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  return asUtc - Math.floor(instant.getTime() / MINUTE_MS) * MINUTE_MS;
}

/** `2026-10-15` + `06:00` read as wall-clock time in `timeZone` → UTC ISO string; null if invalid. */
export function zonedToIso(date: string, time: string, timeZone: string): string | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dateMatch || !timeMatch) return null;
  const [, y, mo, d] = dateMatch.map(Number) as [number, number, number, number];
  const [, h, mi] = timeMatch.map(Number) as [number, number, number];
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const offset = zoneOffsetMs(new Date(guess), timeZone);
  return new Date(guess - offset).toISOString();
}

/** UTC ISO → `{date: "2026-10-15", time: "06:00"}` in `timeZone` (prefills the schedule inputs). */
export function isoToZoned(iso: string, timeZone: string): { date: string; time: string } {
  const p = zonedParts(new Date(iso), timeZone);
  const pad = (n: number) => String(n).padStart(2, "0");
  return { date: `${String(p.year)}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}` };
}

/** Countdown for "Sends in …": whole days when ≥ 1 day away, else hours (min 0). */
export function sendsIn(iso: string, now: number): { unit: "days" | "hours"; count: number } {
  const diff = Math.max(new Date(iso).getTime() - now, 0);
  if (diff >= DAY_MS) return { unit: "days", count: Math.ceil(diff / DAY_MS) };
  return { unit: "hours", count: Math.ceil(diff / HOUR_MS) };
}

/** "Forget after" choice → expiry instant relative to the send time; null = never. */
export function expiryFor(option: ExpiryOption, sendAt: number, timeZone: string): string | null {
  if (option === "never") return null;
  if (option === "2d") return new Date(sendAt + 2 * DAY_MS).toISOString();
  if (option === "1w") return new Date(sendAt + 7 * DAY_MS).toISOString();
  const p = zonedParts(new Date(sendAt), timeZone);
  const lastDay = new Date(Date.UTC(p.year, p.month, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  return zonedToIso(`${String(p.year)}-${pad(p.month)}-${pad(lastDay)}`, "23:59", timeZone);
}
